/**
 * Snapshot e diff de árvores de tarefas para o sistema de undo/redo.
 *
 * A estratégia: antes de uma mutação, capturamos um clone profundo da árvore
 * (`cloneTaskTree`). No undo, comparamos a árvore-alvo (o estado anterior) com a
 * árvore atual (`diffTaskTrees`) e geramos a lista mínima de operações de
 * persistência (atualizar / recriar / excluir) para o banco voltar ao alvo.
 *
 * Por que diff em vez de inverso manual por operação: as mutações do Gantt
 * cascateiam para sucessoras e épicos pai. Restaurar por snapshot reverte toda
 * a cascata de uma vez, sem precisar escrever o inverso de cada regra de negócio.
 */

import type { Task } from '../types';

/** Nó achatado com o pai e a posição (índice entre irmãos). */
interface FlatNode {
  task: Task;
  parentId: string | null;
  position: number;
}

/** Operações de persistência para reconciliar o banco com a árvore-alvo. */
export interface TaskDiff {
  toDelete: string[];
  toRestore: Array<{ task: Task; parentId: string | null; position: number }>;
  toUpdate: Array<{ id: string; payload: Record<string, unknown> }>;
}

/**
 * Campos escalares persistíveis comparados no diff.
 *
 * `kanban_status` saiu daqui em 24/08/2026: o campo está morto (a coluna do
 * cartão é `tasks.status`, resolvida contra `kanban_columns`) e mantê-lo no
 * diff fazia o Ctrl+Z RESSUSCITAR um valor legado que ninguém mais escreve.
 *
 * `status` fica, e de propósito: desfazer uma mudança de coluna é exatamente o
 * que o usuário espera do Ctrl+Z. Isso só é seguro porque o quadro é um por
 * projeto e todo id de coluna do snapshot pertence ao mesmo quadro. No dia em
 * que existirem vários quadros por projeto, `status` tem que sair daqui junto
 * com o `board_id`, e o movimento de coluna vira uma ação de undo própria:
 * senão um Ctrl+Z de algo feito no Gantt passa a arrastar cartão entre
 * quadros, e o cartão fica com um status que não existe no quadro dele.
 */
const SIMPLE_FIELDS = [
  'name', 'type', 'duration', 'progress', 'color', 'assigned', 'priority',
  'description', 'expanded', 'status', 'kanban_position',
  'sprint_id', 'estimated_hours'
] as const;

/**
 * Achata a árvore INCLUINDO subárvores recolhidas (diferente de flattenTasks,
 * que respeita `expanded`). Para snapshot precisamos de todos os descendentes.
 */
const flattenFull = (tasks: Task[], parentId: string | null, out: FlatNode[]): void => {
  tasks.forEach((task, index) => {
    out.push({ task, parentId, position: index });
    if (task.children && task.children.length > 0) {
      flattenFull(task.children, task.id, out);
    }
  });
};

/** Clone profundo preservando objetos Date. */
export const cloneTaskTree = (tasks: Task[]): Task[] => {
  return structuredClone(tasks);
};

/**
 * Reescreve `position` de cada tarefa para o seu índice entre irmãos.
 * Mantém a ordem em memória coerente com o que será persistido.
 */
export const normalizePositions = (tasks: Task[]): Task[] => {
  return tasks.map((task, index) => {
    const children = task.children && task.children.length > 0
      ? normalizePositions(task.children)
      : task.children;
    return { ...task, position: index, children } as Task;
  });
};

const formatDate = (value: unknown): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  if (isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Monta o payload persistível de um nó (camelCase onde o taskService converte). */
const buildPayload = (node: FlatNode): Record<string, unknown> => {
  const t = node.task as unknown as Record<string, unknown>;
  const payload: Record<string, unknown> = {};
  for (const field of SIMPLE_FIELDS) {
    payload[field] = t[field] ?? null;
  }
  payload.parent_id = node.parentId ?? null;
  payload.position = node.position;
  payload.startDate = t.startDate ?? null;
  payload.endDate = t.endDate ?? null;
  payload.dependencies = t.dependencies ?? [];
  payload.dependencyLags = t.dependencyLags ?? t.dependency_lags ?? {};
  payload.customFields = t.customFields ?? t.custom_fields ?? {};
  return payload;
};

/** Assinatura normalizada para comparar dois payloads (datas e objetos estáveis). */
const signature = (payload: Record<string, unknown>): string => {
  const norm: Record<string, unknown> = { ...payload };
  norm.startDate = formatDate(payload.startDate);
  norm.endDate = formatDate(payload.endDate);
  norm.dependencies = JSON.stringify(payload.dependencies ?? []);
  norm.dependencyLags = JSON.stringify(payload.dependencyLags ?? {});
  norm.customFields = JSON.stringify(payload.customFields ?? {});
  return JSON.stringify(norm);
};

/**
 * Compara a árvore-alvo (estado que queremos restaurar) com a árvore atual.
 * Itera o alvo em ordem (pais antes de filhos) para que recriações respeitem FKs.
 */
export const diffTaskTrees = (target: Task[], current: Task[]): TaskDiff => {
  const targetFlat: FlatNode[] = [];
  const currentFlat: FlatNode[] = [];
  flattenFull(target, null, targetFlat);
  flattenFull(current, null, currentFlat);

  const targetMap = new Map(targetFlat.map(n => [n.task.id, n]));
  const currentMap = new Map(currentFlat.map(n => [n.task.id, n]));

  const toDelete: string[] = [];
  for (const node of currentFlat) {
    if (!targetMap.has(node.task.id)) toDelete.push(node.task.id);
  }

  const toRestore: TaskDiff['toRestore'] = [];
  const toUpdate: TaskDiff['toUpdate'] = [];
  for (const node of targetFlat) {
    const currentNode = currentMap.get(node.task.id);
    if (!currentNode) {
      toRestore.push({ task: node.task, parentId: node.parentId, position: node.position });
    } else if (signature(buildPayload(node)) !== signature(buildPayload(currentNode))) {
      toUpdate.push({ id: node.task.id, payload: buildPayload(node) });
    }
  }

  return { toDelete, toRestore, toUpdate };
};
